// ABOUTME: Collects metadata-driven details required for a job status change.
// ABOUTME: Initializes status dates consistently before submitting the change.
import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import useGetMetadata from "customHooks/useGetMetadata";
import { useTranslation } from "react-i18next";
import useValuesFromSource from "customHooks/useValuesFromSource";
import useCompleoRHF from "customHooks/useCompleoReactHookForm";
import { ApiStateType, useApiCache } from "customHooks/useApi";
import FormModal from "customHooks/useCompleoReactHookForm/components/FormModal";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import { Grid } from "@material-ui/core";
import { useAuthState } from "_ReactContext/AuthContext";
import useJobRequestLastRequestCorrectionReason from "./useJobHistory";
import {
    getApplicantsInHiredGroup,
    getJobStatusChangeInitialOpeningDate,
    getJobStatusChangeModule
} from "./statusHelpers";

interface IModalNewMail {
    open: boolean;
    handleClose: () => void;
    jobData: Compleo.IObject;
    newStatus: CompleoShared.Common.JobStatus;
    oldStatus: CompleoShared.Common.JobStatus;
    editStatusMain: (
        newStatus: CompleoShared.Common.JobStatus,
        otherValues?: Compleo.IObject | undefined
    ) => Promise<void>;
}

const ModalChangeStatus = (props: IModalNewMail) => {
    const { open, handleClose, jobData, newStatus, editStatusMain, oldStatus } =
        props;
    const [isUpdating, setIsUpdating] = React.useState(false);
    const { company } = useAuthState();

    const module = getJobStatusChangeModule(newStatus, oldStatus);
    const [metadata] = useGetMetadata(module);

    const disapprovedReason = useJobRequestLastRequestCorrectionReason(
        jobData.pk.split(":")[1],
        newStatus
    );

    const [t, i18n, readyTranslation] = useLocalTranslation(module);

    const [initialValues] = useValuesFromSource(metadata, true);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    initialValues.title = jobData.title;
    let otherDataReady = false;
    const jobId = jobData.pk.split(":")[1];

    const postAddressSearch = `/job/getapplicantsbyjobstage`;
    const [getJobInfoReturn, getJobInfo] = useApiCache(
        postAddressSearch,
        "post",
        {
            jobId,
            companyId: company.companyId
        },
        true
    );

    React.useEffect(() => {
        if (newStatus === "finished") {
            getJobInfo();
        }
    }, [newStatus]);

    if (newStatus === "finished") {
        const jobDataGroups =
            getJobInfoReturn.response?.data?.applicantsByGroup || [];
        const hiredValue = getApplicantsInHiredGroup(jobDataGroups, jobId);

        initialValues.numberOfPositions = jobData.numberOfPositions || 0;
        initialValues.applicantsInHiredGroup = hiredValue;
        initialValues.closingDate = currentDate;
        otherDataReady = getJobInfoReturn.status === "success";
    } else {
        otherDataReady = true;
    }
    if (newStatus === "canceled") {
        initialValues.cancellationDate = currentDate;
    }
    if (newStatus === "open") {
        initialValues.openingDate = getJobStatusChangeInitialOpeningDate(
            oldStatus,
            currentDate,
            jobData?.openingDate
        );
    }
    if (newStatus === "approvedRequest") {
        initialValues.approvalDate = currentDate;
    }
    if (newStatus === "disapprovedRequest") {
        initialValues.disapprovalDate = currentDate;
    }
    if (newStatus === "suspended") {
        initialValues.lastSuspensionDate = currentDate;
    }
    if (newStatus === "requested" && disapprovedReason) {
        initialValues.disapprovedReason = disapprovedReason;
    }
    if (newStatus === "awaitingCorrectionRequest") {
        initialValues.lastCorrectionJobRequestDate = currentDate;
    }
    if (newStatus === "jobApprovalRequested") {
        initialValues.branch = jobData.branch;
        initialValues.customer = jobData.customer;
        initialValues.category = jobData.category;
        initialValues.jobApprovalSettings = {
            selectedJobApproval:
                jobData?.jobApprovalSettings?.selectedJobApproval
        };
    }

    const ready =
        readyTranslation && metadata.status === "success" && otherDataReady;

    const handleSubmit = async (values: any) => {
        const retorno: Compleo.IObject = {};
        retorno.status = 200;
        try {
            setIsUpdating(true);
            await editStatusMain(newStatus, values);
            setIsUpdating(false);
            handleClose();
            return retorno;
        } catch (ex) {
            setIsUpdating(false);
            return ex.response;
        }
    };

    const postReturn: ApiStateType = {
        status: "success",
        response: {},
        exception: null
    };

    const [formCompleo, finished, handleSubmitRHF] = useCompleoRHF({
        t: t,
        ready: ready,
        i18nV: i18n,
        postMethod: handleSubmit,
        postReturn: postReturn,
        FormType: FormModal,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues,
        formGroupPaperElevation: 0
    });
    let FormReturn: JSX.Element = <Loading />;
    if (finished && ready) {
        FormReturn = formCompleo;
    }
    const handleSave = async () => {
        await handleSubmitRHF();
    };

    if (finished && ready) {
        return (
            <Dialog
                fullWidth
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth="md"
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("a_FeatureMainDescription")}
                </DialogTitle>
                <DialogContent>
                    <Grid container>
                        <Grid item container xs={12}>
                            {FormReturn}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => handleClose()}
                        color="secondary"
                        disabled={isUpdating}
                    >
                        {t("COMPLEOGENERAL_CANCEL")}
                    </Button>
                    <Button
                        onClick={() => handleSave()}
                        variant="contained"
                        color="primary"
                        disabled={isUpdating}
                    >
                        {t("btnChangeStatus")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    } else {
        return (
            <Dialog
                fullWidth
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth="md"
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
            >
                <DialogContent>
                    <Grid container>
                        <Grid item container xs={12}>
                            <Loading />
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        );
    }
};

const useLocalTranslation = (
    module: string
): [typeof tLocal, typeof i18n, typeof readyTranslation] => {
    const modules: string[] = [module];
    let additionalModule: string | undefined = undefined;
    if (
        module ===
        "CHANGEJOBSTATUSFROMJOBAPPROVALREJECTEDTOJOBAPPROVALREQUESTED"
    ) {
        additionalModule = "JOBREQUESTNEW";
        modules.push(additionalModule);
    }
    const [tLocal, i18n, readyTranslation] = useTranslation(modules, {
        useSuspense: false
    });

    if (modules.length === 1) {
        return [tLocal, i18n, readyTranslation];
    }
    const t = (key: string, otherParams?: any) => {
        return tLocal(
            key,
            tLocal(`${additionalModule}:${key}`, otherParams),
            otherParams
        );
    };
    return [t, i18n, readyTranslation];
};

export default ModalChangeStatus;
